import React, { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { List as LoadingState } from 'react-content-loader';
import { useHistory, useLocation } from 'react-router-dom';
import Input from 'components/inputs/Input';
import Button from 'components/buttons/Button';
import SectionWrapper from 'pages/Layout/SectionWrapper';
import {
  Filter,
  StyleTable,
  HeaderSection,
  ButtonSection,
  FilterSelectWrapper
} from '../Styled';
import { selectUser } from 'redux/slices/userSlice';
import {
  getAdministrators,
  AdminFilterField
} from 'services/rest/SuperAdmin/Administrator/getAdministrators';
import AdministratorsTable from './AdministratorsTable';
import TableFooter from 'components/TableFooter';
import { SuccessAlert } from 'components/Alert';

const Administrator = () => {
  const { t } = useTranslation(['super_admin_administrator']);
  const history = useHistory();
  const { currentUser } = useSelector(selectUser);
  const [loading, setLoading] = useState(true);
  const [administrators, setAdministrators] = useState<any | null>(null);
  const [total, setTotal] = useState(0);

  const [filterText, setFilterText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [orderBy, setOrderBy] = useState('id-asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(10);
  const paginate = (pageNumber: any) => setCurrentPage(pageNumber);

  const location = useLocation();
  const { state } = location as any;
  const [backendSuccess, setBackendSuccess] = useState<any | null>(null);

  const showAlertSuccessCompanyCreated = async () => {
    if (typeof state === 'string') {
      setBackendSuccess(state);
    }
  };

  const mapStatusFilterToField = (s: string): AdminFilterField => {
    switch (s) {
      case 'id':
        return 'id';
      case 'username':
        return 'username';
      case 'name':
        return 'name';
      case 'company':
        return 'companyName';
      case 'all':
      default:
        return 'search';
    }
  };

  useEffect(() => {
    const fetchAdmins = async () => {
      const filter = filterText
        ? { field: mapStatusFilterToField(statusFilter), value: filterText }
        : undefined;
      const { data, total } = await getAdministrators(
        currentUser?.token,
        postsPerPage,
        currentPage,
        filter
      );
      setAdministrators(data ?? []);
      setTotal(total ?? 0);
      showAlertSuccessCompanyCreated();
      setLoading(false);
    };
    const t = setTimeout(fetchAdmins, 300);
    return () => clearTimeout(t);
  }, [currentPage, filterText, statusFilter, currentUser?.token, postsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterText, statusFilter]);

  const getFilterColor = (filter: string) => {
    switch (filter) {
      case 'id':
        return '#1a4dcc'; // azul
      case 'name':
        return '#1a4dcc';
      case 'username':
        return '#1a4dcc';
      case 'company':
        return '#1a4dcc';
      default:
        return '#222';
    }
  };

  const getSortedAdministrators = (admins: any[]) => {
    if (!admins) return [];
    const sorted = [...admins];
    switch (orderBy) {
      case 'id-asc':
        sorted.sort((a, b) => a.id - b.id);
        break;
      case 'id-desc':
        sorted.sort((a, b) => b.id - a.id);
        break;
      case 'name-asc':
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        sorted.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'username-asc':
        sorted.sort((a, b) => a.username.localeCompare(b.username));
        break;
      case 'username-desc':
        sorted.sort((a, b) => b.username.localeCompare(a.username));
        break;
      case 'company-asc':
        sorted.sort((a, b) => a.company.name.localeCompare(b.company.name));
        break;
      case 'company-desc':
        sorted.sort((a, b) => b.company.name.localeCompare(a.company.name));
        break;
      default:
        break;
    }
    return sorted;
  };

  // NOTE: ordena solo la página visible. Para sort global hace falta extender
  // el backend con `?orderBy=` y mover el sort allá. Ticket aparte.
  const sortedAdministrators = getSortedAdministrators(administrators);

  if (loading) {
    return <LoadingState height="230px" width="100%" />;
  }

  return (
    <>
      {backendSuccess && (
        <SuccessAlert
          text={backendSuccess}
          allowClose={true}
          onClose={() => setBackendSuccess(null)}
        />
      )}

      <Suspense fallback={<LoadingState height="230px" width="100%" />}>
        <SectionWrapper>
          <StyleTable>
            <HeaderSection>
              <Filter>
                <Input
                  type="search"
                  placeholder={t('super_admin_administrator:search')}
                  value={filterText}
                  onChange={e => {
                    setFilterText(e.target.value);
                  }}
                />
                <FilterSelectWrapper>
                  <label>Filtrar por</label>
                  <select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value as any)}
                    style={{
                      color: getFilterColor(statusFilter),
                      fontWeight: 'bold'
                    }}
                  >
                    <option value="all">TODAS</option>
                    <option value="id">ID</option>
                    <option value="username">USUARIO</option>
                    <option value="name">NOMBRE</option>
                    <option value="company">COMPAÑIA</option>
                  </select>
                </FilterSelectWrapper>

                <FilterSelectWrapper focusColor="#1a4dcc">
                  <label>Ordenar Por</label>
                  <select
                    value={orderBy}
                    onChange={e => setOrderBy(e.target.value)}
                  >
                    <option value="id-asc">ID (menor a mayor)</option>
                    <option value="id-desc">ID (mayor a menor)</option>
                    <option value="username-asc">Usuario (A-Z)</option>
                    <option value="username-desc">Usuario (Z-A)</option>
                    <option value="name-asc">Nombre (A-Z)</option>
                    <option value="name-desc">Nombre (Z-A)</option>
                    <option value="company-asc">Compañía (A-Z)</option>
                    <option value="company-desc">Compañía (Z-A)</option>
                  </select>
                </FilterSelectWrapper>
              </Filter>
              <ButtonSection>
                <Button
                  value={t('super_admin_administrator:create_admin')}
                  variant="primary"
                  onClick={() =>
                    history.replace({ pathname: `/administrators/new` })
                  }
                />
              </ButtonSection>
            </HeaderSection>
            <AdministratorsTable
              administrators={sortedAdministrators}
              statusFilter={statusFilter}
              getFilterColor={getFilterColor}
            />
            <TableFooter
              postsPerPage={postsPerPage}
              totalPosts={total}
              paginate={paginate}
              currentPage={currentPage}
            />
          </StyleTable>
        </SectionWrapper>
      </Suspense>
    </>
  );
};

export default Administrator;
